import { useState } from 'react';
import { Category } from '../types';

interface CategoryListProps {
  categories: Category[];
  loading: boolean;
  onEdit: (category: Category) => void;
  onDelete: (id: string) => void;
}

export function CategoryList({ categories, loading, onEdit, onDelete }: CategoryListProps) {
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

  const handleDelete = (id: string) => {
    if (deleteConfirm === id) {
      onDelete(id);
      setDeleteConfirm(null);
    } else {
      setDeleteConfirm(id);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-24 gap-6" role="status" aria-label="Carregando categorias">
        <div className="relative">
          <div className="w-16 h-16 rounded-full border-4 border-blue-500/20 border-t-blue-500 animate-spin"></div>
          <div className="absolute inset-0 bg-blue-500/10 rounded-full blur-xl animate-pulse"></div>
        </div>
        <p className="text-[10px] font-bold text-gray-500 uppercase tracking-[0.2em] font-oswald animate-pulse">Carregando categorias...</p>
      </div>
    );
  }

  if (categories.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-24 text-center px-10">
        <div className="w-20 h-20 bg-white/5 rounded-[2rem] flex items-center justify-center border border-white/10 mb-6 group-hover:scale-110 transition-transform duration-500">
          <svg className="w-10 h-10 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
          </svg>
        </div>
        <h3 className="text-xl font-bold text-white font-oswald uppercase tracking-tight mb-2">Nenhuma categoria</h3>
        <p className="text-gray-500 text-sm max-w-xs leading-relaxed uppercase font-bold text-[10px] tracking-widest">
          Você ainda não criou nenhuma categoria para seus contatos.
        </p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto custom-scrollbar">
      <table className="min-w-full border-separate border-spacing-0" role="table">
        <thead className="sticky top-0 z-10">
          <tr className="bg-[#1e293b]/80 backdrop-blur-md">
            <th className="px-10 py-5 text-left text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] font-oswald border-b border-white/10">
              Cor
            </th>
            <th className="px-10 py-5 text-left text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] font-oswald border-b border-white/10">
              Nome
            </th>
            <th className="px-10 py-5 text-left text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] font-oswald border-b border-white/10">
              Descrição
            </th>
            <th className="px-10 py-5 text-left text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] font-oswald border-b border-white/10">
              Atualizado em
            </th>
            <th className="px-10 py-5 text-right text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] font-oswald border-b border-white/10">
              Ações
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-white/5">
          {categories.map((category) => (
            <tr key={category.id} className="group hover:bg-white/[0.02] transition-colors">
              <td className="px-10 py-6 whitespace-nowrap">
                <div
                  className="w-8 h-8 rounded-xl border-2 border-white/10 shadow-lg group-hover:scale-110 transition-transform duration-300"
                  style={{ backgroundColor: category.cor }}
                  aria-label={`Cor da categoria: ${category.cor}`}
                />
              </td>
              <td className="px-10 py-6 whitespace-nowrap">
                <div className="text-sm font-bold text-white uppercase tracking-wide font-oswald">
                  {category.nome}
                </div>
              </td>
              <td className="px-10 py-6">
                <div className="text-[11px] font-medium text-gray-400 uppercase tracking-wider max-w-xs truncate">
                  {category.descricao || '-'}
                </div>
              </td>
              <td className="px-10 py-6 whitespace-nowrap">
                <div className="text-[10px] font-bold text-gray-500 uppercase tracking-widest font-oswald">
                  {formatDate(category.atualizadoEm)}
                </div>
              </td>
              <td className="px-10 py-6 whitespace-nowrap text-right">
                <div className="flex justify-end gap-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <button
                    onClick={() => onEdit(category)}
                    className="p-3 text-blue-400 hover:text-white hover:bg-blue-600/20 rounded-xl transition-all border border-blue-500/10 hover:border-blue-500/30"
                    aria-label={`Editar categoria ${category.nome}`}
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                  </button>
                  <button
                    onClick={() => handleDelete(category.id)}
                    className={`p-3 rounded-xl transition-all border ${
                      deleteConfirm === category.id
                        ? 'bg-red-600 text-white border-red-500 animate-pulse'
                        : 'text-red-400 hover:text-white hover:bg-red-600/20 border-red-500/10 hover:border-red-500/30'
                    }`}
                    aria-label={
                      deleteConfirm === category.id
                        ? `Confirmar exclusão de ${category.nome}`
                        : `Excluir categoria ${category.nome}`
                    }
                  >
                    {deleteConfirm === category.id ? (
                      <span className="text-[10px] font-bold uppercase tracking-widest px-2 font-oswald">Confirmar?</span>
                    ) : (
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-4v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    )}
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}